import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, MessageCircle, ShoppingCart, AlertTriangle, Award, Tag, Palette } from "lucide-react";
import WhatsAppSettings from "./WhatsAppSettings";
import LoyaltySettings from "./LoyaltySettings";
import DiscountPolicySettings from "./DiscountPolicySettings";
import ThemeSettings from "./ThemeSettings";

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const DEFAULT_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];

/**
 * System Settings page — hosts every tenant-scoped configuration in tabs.
 * WhatsApp settings live here (moved out of the Platform menu) so each salon
 * owner manages their own messaging integration independently.
 */
export default function Settings() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("slots");
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({ day_of_week: 0, specific_date: "", time_slot: "10:00", capacity: 2, mode: "day" });
  const [defaultCapacity, setDefaultCapacity] = useState(2);
  // App-level operational toggles loaded once when the "POS" tab opens.
  const [appSettings, setAppSettings] = useState(null);
  const [savingApp, setSavingApp] = useState(false);

  const loadSlots = async () => { const r = await api.get("/slot-config"); setConfigs(r.data); };
  useEffect(() => { if (tab === "slots") loadSlots(); }, [tab]);

  const loadAppSettings = async () => {
    try {
      const r = await api.get("/app-settings");
      setAppSettings(r.data);
      const cap = parseInt(r.data?.default_slot_capacity, 10);
      if (cap >= 1) {
        setDefaultCapacity(cap);
        setForm((prev) => ({ ...prev, capacity: cap }));
      }
    }
    catch { setAppSettings({}); }
  };
  useEffect(() => {
    if ((tab === "pos" || tab === "slots") && !appSettings) loadAppSettings();
  }, [tab]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveAppSettings = async (patch) => {
    setSavingApp(true);
    try {
      const r = await api.put("/app-settings", patch);
      setAppSettings(r.data);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch {
      toast.error(t("error_generic"));
    } finally { setSavingApp(false); }
  };

  const submit = async () => {
    const payload = {
      day_of_week: form.mode === "day" ? parseInt(form.day_of_week) : null,
      specific_date: form.mode === "date" ? form.specific_date : null,
      time_slot: form.time_slot,
      capacity: parseInt(form.capacity, 10) || defaultCapacity,
    };
    await api.post("/slot-config", payload);
    toast.success(lang === "ar" ? "تم الحفظ" : "Saved"); loadSlots();
  };

  const remove = async (id) => { await api.delete(`/slot-config/${id}`); loadSlots(); };

  const TABS = [
    { id: "slots", label: lang === "ar" ? "إعدادات الحجوزات" : "Booking Slots", icon: Calendar },
    { id: "pos", label: lang === "ar" ? "نقطة البيع والمخزون" : "POS & Inventory", icon: ShoppingCart },
    { id: "discounts", label: lang === "ar" ? "سياسة الخصومات" : "Discount Policy", icon: Tag },
    { id: "loyalty", label: lang === "ar" ? "نظام الولاء" : "Loyalty Program", icon: Award },
    { id: "whatsapp", label: lang === "ar" ? "إعدادات واتساب" : "WhatsApp", icon: MessageCircle },
    { id: "theme", label: lang === "ar" ? "الثيم والألوان" : "Theme & Colors", icon: Palette },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-heading text-4xl gold-text">{t("nav_settings")}</h1>

      <div className="flex flex-wrap gap-2 border-b border-[var(--border)] pb-1">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`settings-tab-${tb.id}`}
            className={`px-4 py-2 rounded-t-md text-sm flex items-center gap-2 transition ${
              tab === tb.id
                ? "bg-[var(--gold)]/10 text-[var(--gold)] border-b-2 border-[var(--gold)] -mb-[3px]"
                : "text-[var(--text-muted)] hover:text-[var(--text)]"
            }`}>
            <tb.icon size={14}/>{tb.label}
          </button>
        ))}
      </div>

      {tab === "slots" && (
        <div className="card-surface p-5 space-y-4">
          <h3 className="font-heading text-xl text-[var(--text)]">{t("slot_config")}</h3>
          <p className="text-sm text-[var(--text-muted)]">
            {lang === "ar"
              ? "بدون تخصيصات يدوية، الأوقات الافتراضية هي 10:00–23:30 كل 30 دقيقة. عيّن السعة الافتراضية أدناه (مثلاً 10 حجوزات لكل فترة)."
              : "With no manual overrides, times default to 10:00–23:30 every 30 minutes. Set the default capacity below (e.g. 10 bookings per slot)."}
          </p>

          <div className="border border-[var(--border-subtle)] rounded-xl p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-[var(--text-muted)] block mb-1">
                {lang === "ar" ? "السعة الافتراضية لكل فترة" : "Default capacity per slot"}
              </label>
              <input
                type="number"
                min="1"
                max="100"
                className="input-dark w-full max-w-[120px]"
                value={defaultCapacity}
                onChange={(e) => setDefaultCapacity(parseInt(e.target.value, 10) || 1)}
                data-testid="default-slot-capacity"
              />
            </div>
            <button
              type="button"
              onClick={() => saveAppSettings({ default_slot_capacity: defaultCapacity })}
              disabled={savingApp}
              className="btn-gold px-4 py-2 rounded-md text-sm"
              data-testid="save-default-slot-capacity"
            >
              {savingApp ? "..." : (lang === "ar" ? "حفظ السعة الافتراضية" : "Save default capacity")}
            </button>
            <p className="text-xs text-[var(--text-muted)] w-full">
              {lang === "ar"
                ? `القيمة الحالية: ${appSettings?.default_slot_capacity ?? 2}. التخصيصات أدناه تتفوق على هذا الافتراضي ليوم أو تاريخ محدد.`
                : `Current value: ${appSettings?.default_slot_capacity ?? 2}. Overrides below take precedence for a specific day or date.`}
            </p>
          </div>

          <p className="text-sm text-[var(--text-muted)]">
            {lang === "ar" ? "تخصيصات اختيارية لأيام أو تواريخ محددة:" : "Optional overrides for specific days or dates:"}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select className="input-dark" value={form.mode} onChange={e => setForm({...form, mode: e.target.value})}>
              <option value="day">{t("day_of_week")}</option>
              <option value="date">{t("specific_date")}</option>
            </select>
            {form.mode === "day" ? (
              <select className="input-dark" value={form.day_of_week} onChange={e => setForm({...form, day_of_week: e.target.value})}>
                {DAYS.map((d, i) => <option key={d} value={i}>{t(d)}</option>)}
              </select>
            ) : (
              <input type="date" className="input-dark" value={form.specific_date} onChange={e => setForm({...form, specific_date: e.target.value})}/>
            )}
            <select className="input-dark" value={form.time_slot} onChange={e => setForm({...form, time_slot: e.target.value})}>
              {DEFAULT_SLOTS.map(s => <option key={s}>{s}</option>)}
            </select>
            <input className="input-dark" type="number" min="1" value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} placeholder={t("capacity")}/>
            <button onClick={submit} className="btn-gold px-4 py-2 rounded-md flex items-center gap-2 justify-center"><Plus size={14}/>{t("apply")}</button>
          </div>

          <div className="space-y-2">
            {configs.map(c => (
              <div key={c.slot_config_id} className="flex items-center justify-between border border-[var(--border-subtle)] rounded p-3">
                <div className="text-sm text-[var(--text)]">
                  {c.specific_date ? c.specific_date : t(DAYS[c.day_of_week])} · {c.time_slot} · {t("capacity")}: {c.capacity}
                </div>
                <button onClick={() => remove(c.slot_config_id)} className="p-1 border border-[var(--border)] rounded hover:border-red-500"><Trash2 size={12}/></button>
              </div>
            ))}
            {configs.length === 0 && <div className="text-sm text-[var(--text-muted)]">{t("no_data")}</div>}
          </div>
        </div>
      )}

      {tab === "whatsapp" && <WhatsAppSettings/>}

      {tab === "loyalty" && <LoyaltySettings/>}

      {tab === "discounts" && <DiscountPolicySettings/>}

      {tab === "theme" && <ThemeSettings/>}

      {tab === "pos" && (
        <div className="card-surface p-5 space-y-5" data-testid="pos-settings-panel">
          <div>
            <h3 className="font-heading text-xl text-[var(--text)]">
              {lang === "ar" ? "نقطة البيع والمخزون" : "POS & Inventory"}
            </h3>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {lang === "ar"
                ? "خيارات تشغيلية تتحكم في سلوك نقطة البيع وحركة المخزون."
                : "Operational toggles that govern POS behaviour and stock movements."}
            </p>
          </div>

          {/* Allow negative stock toggle */}
          <div className="border border-[var(--border-subtle)] rounded-xl p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
              <AlertTriangle size={18}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="toggle-allow-negative-stock" className="font-semibold text-[var(--text)] cursor-pointer">
                  {lang === "ar" ? "السماح بالمخزون السالب" : "Allow negative stock"}
                </label>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    id="toggle-allow-negative-stock"
                    data-testid="toggle-allow-negative-stock"
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!appSettings?.allow_negative_stock}
                    disabled={!appSettings || savingApp}
                    onChange={(e) => saveAppSettings({ allow_negative_stock: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-stone-200 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5 peer-disabled:opacity-50"/>
                </label>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1.5 leading-relaxed">
                {lang === "ar"
                  ? "عند التفعيل، يمكن إصدار فواتير لمنتجات لا يوجد منها رصيد كافٍ في الفرع، وستهبط كمية المخزون إلى أرقام سالبة. مفيد لو كنت تستلم البضاعة لاحقاً وتريد البيع الآن. ❗ يُنصح بالمتابعة الدورية للمخزون السالب من تقرير المخزون."
                  : "When enabled, the POS can issue invoices for products even when the branch has insufficient stock, and the on-hand quantity will go negative. Useful when shipments arrive after the sale; reconcile through the inventory report. ❗ Review negative balances regularly."}
              </p>
              {appSettings?.allow_negative_stock && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>
                  {lang === "ar" ? "مُفعّل — تحقق من الرصيد بانتظام" : "Enabled — monitor balances"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
